import {
  Box,
  Checkmark,
  Crossmark,
  DISABLED_REASONABILITY_CHECK_STRATEGIES_SET,
  Dialog,
  Divider,
  EMPTY_ARRAY,
  FormattedMessage,
  HStack,
  InlineFormattedNumber,
  MixpanelEvent,
  MixpanelEventSource,
  NewOrderForm,
  NotificationVariants,
  OrdStatusEnum,
  Spinner,
  Text,
  Toasts,
  VStack,
  archiveOrderID,
  calculateMidPrice,
  cleanState,
  format,
  isCFD,
  logger,
  modifyOrder,
  resubmitOrderForm,
  selectBalances,
  selectError,
  selectFocusedOrderID,
  selectForm,
  selectIsFormValid,
  selectLimitPrice,
  selectModifiedOrder,
  selectOrderStep,
  selectOrderViewType,
  selectQuote,
  selectQuoteReqID,
  selectTradingLimitsValidation,
  setError,
  setFocusedOrderID,
  setMarketAccount,
  setOrderCurrency,
  setQuantity,
  setSide,
  setStrategy,
  setStrategyParams,
  setViewType,
  touchAll,
  touchField,
  unsetQuoteReqID,
  useCurrenciesContext,
  useDisclosure,
  useDynamicCallback,
  useMarketDataSnapshot,
  useMixpanel,
  useOMSDependencies,
  useObservableValue,
  useOrderDependencies,
  useRFQServiceContext,
  useToasts,
  useWLCanCancelCustomerOrder,
  useWLCanModifyCustomerOrder,
  useWLOrder,
  useWLOrderFormDispatch,
  useWLOrderFormSelector,
  useWLOrderServiceContext,
  useWLSymbol,
  validateMidPriceDiff,
  type CustomerOrder,
  type MarketDataSnapshot,
  type NewOrderFormProps,
  type OrderFormSides,
  type OrderStrategy,
  type Security,
  type WLRootState,
} from '@talos/kyoko';
import type { BigSource } from 'big.js';
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { scan } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { useGlobalCancelDialog } from '../../providers';
import { OMS_DIALOG_WIDTH } from '../../styles/dimensions';
import { useShouldRenderMobile } from '../Layout/useShouldRenderMobile';
import { BalanceDetails } from './BalanceDetails';
import { ExecutionCard } from './ExecutionCard';
import { OrderSummary } from './OrderSummary';
import { PickQuoteView } from './PickQuoteView';
import { FeedbackMessage, MessageWrapper, OVERLAY_Z_INDEX, OmsToasts, OrderFormsWrapper, Overlay } from './styles';

/**
 * When placing order, this enum is used to determine the step of validation to perform.
 * For example, clicking place order in order form, or in a midprice modal, or in order size modal.
 * Once the validation is NONE, no checks are made and the order is sent.
 */
const CheckValidationStep = {
  MIDPRICE: 0,
  ORDSIZE: 1,
  NONE: 2,
} as const;
type CheckValidationStep = (typeof CheckValidationStep)[keyof typeof CheckValidationStep];

const getStrategiesGivenSecurity = (s: Security) => s.SupportedStrategies ?? [];
export const OrderForms = () => {
  useOMSDependencies({ getStrategiesGivenSecurity });
  return <NewOrderForms />;
};

const messages = defineMessages({
  sendingOrder: {
    defaultMessage: 'Sending order',
    id: 'OrderForms.sendingOrder',
  },
  orderSent: {
    defaultMessage: 'Order sent',
    id: 'OrderForms.orderSent',
  },
  orderFilled: {
    defaultMessage: 'Order filled',
    id: 'OrderForms.orderFilled',
  },
  quoteExpired: {
    defaultMessage: 'Quote expired',
    id: 'OrderForms.quoteExpired',
  },
  orderUpdated: {
    defaultMessage: 'Order updated',
    id: 'OrderForms.orderUpdated',
  },
  orderRejected: {
    defaultMessage: 'Order rejected',
    id: 'OrderForms.orderRejected',
  },
  modifyRejected: {
    defaultMessage: 'Modification rejected',
    id: 'OrderForms.modifyRejected',
  },
  cancelFailed: {
    defaultMessage: 'Cancel failed',
    id: 'OrderForms.cancelFailed',
  },
  orderCanceled: {
    defaultMessage: 'Order canceled',
    id: 'OrderForms.orderCanceled',
  },
  cancelOrder: {
    defaultMessage: 'Canceling order',
    id: 'OrderForms.cancelOrder',
  },
  continueConfirmation: {
    defaultMessage: 'Are you sure you want to continue?',
    id: 'OrderForms.continueConfirmation',
  },
  thresholdWarning: {
    defaultMessage: 'Quantity exceeds the threshold. Do you want to proceed with the order?',
    id: 'OrderForms.thresholdWarning',
  },
  warningThreshold: {
    defaultMessage: 'Warning Threshold',
    id: 'OrderForms.warningThreshold',
  },
  midPriceWarning: {
    defaultMessage: `Limit price {limitPrice} {quoteCurrency} is more than 5% away from mid price {midPrice} {quoteCurrency}.`,
    id: 'OrderForms.midPriceWarning',
  },
  slowToRespondToast: {
    defaultMessage: 'The server is slow to respond. If this error persists, please contact support.',
    id: 'OrderForms.slowToRespondToast',
  },
});

const NewOrderForms: FC = () => {
  useOrderDependencies();
  const dispatch = useWLOrderFormDispatch();
  const orderBeingModified = useWLOrderFormSelector(selectModifiedOrder);
  const orderFormValid = useWLOrderFormSelector(selectIsFormValid);
  const orderStep = useWLOrderFormSelector(selectOrderStep);
  const quoteReqID = useWLOrderFormSelector(selectQuoteReqID);
  const maybeQuote = useWLOrderFormSelector(selectQuote);
  const { orderService } = useWLOrderServiceContext();
  const focusedOrderID = useWLOrderFormSelector(selectFocusedOrderID);
  const orderViewType = useWLOrderFormSelector(selectOrderViewType);
  const { warn, limit } = useWLOrderFormSelector(selectTradingLimitsValidation);
  const form = useWLOrderFormSelector(selectForm);
  const { currenciesBySymbol } = useCurrenciesContext();

  const mixpanel = useMixpanel();

  const rfqService = useRFQServiceContext();

  const sendRFQForm = useDynamicCallback(() => {
    mixpanel.track(MixpanelEvent.SendRfq);

    if (orderFormValid) {
      rfqService.requestQuote();
    }
  });

  const sendOrderForm = useDynamicCallback(async () => {
    if (orderBeingModified) {
      mixpanel.track(MixpanelEvent.UpdateOrder, { strategy: form.strategyField.value?.Name });
      await orderService.updateOrder();
    } else {
      mixpanel.track(MixpanelEvent.SendOrder, { strategy: form.strategyField.value?.Name });
      await orderService.requestOrder();
    }
  });

  const handleViewTypeChange: NewOrderFormProps['handleViewTypeChange'] = useCallback(
    viewType => {
      dispatch(setViewType(viewType));
    },
    [dispatch]
  );

  const handleClearForm: NewOrderFormProps['handleClearForm'] = useDynamicCallback(() => {
    if (maybeQuote || quoteReqID) {
      mixpanel.track(MixpanelEvent.CancelRfq);
      rfqService.cancelQuote(maybeQuote?.RFQID, quoteReqID ?? maybeQuote?.QuoteReqID);
      dispatch(unsetQuoteReqID());
    }
    dispatch(cleanState({ keepStrategy: true, keepSymbol: true }));
    dispatch(setFocusedOrderID(null));
  });

  const handleQuantityChange: NewOrderFormProps['handleQuantityChange'] = useCallback(
    (qty: string | undefined) => {
      dispatch(setQuantity(qty));
    },
    [dispatch]
  );
  const handleOrderCurrencyChange: NewOrderFormProps['handleOrderCurrencyChange'] = useCallback(
    (ccy: string) => {
      dispatch(setOrderCurrency(ccy));
    },
    [dispatch]
  );

  const handleSideChange: NewOrderFormProps['handleSideChange'] = useCallback(
    (side: OrderFormSides) => {
      dispatch(setSide(side));
    },
    [dispatch]
  );
  const handleStrategyChange: NewOrderFormProps['handleStrategyChange'] = useCallback(
    (strategy: OrderStrategy | undefined) => {
      dispatch(setStrategy(strategy));
    },
    [dispatch]
  );
  const handleStrategyParamChange: NewOrderFormProps['handleStrategyParamChange'] = useCallback(
    (key: string, value: any) => {
      dispatch(setStrategyParams({ key, value }));
    },
    [dispatch]
  );

  const { setSymbol: setGlobalSymbol } = useWLSymbol();

  const handleSymbolChange: NewOrderFormProps['handleSymbolChange'] = useDynamicCallback(symbol => {
    if (symbol) {
      mixpanel.track(MixpanelEvent.ChangeSymbol, { source: MixpanelEventSource.OrderForm });
      setGlobalSymbol(symbol);
    }
  });

  const handleMarketAccountChange: NewOrderFormProps['handleMarketAccountChange'] = useCallback(
    account => {
      dispatch(setMarketAccount(account));
    },
    [dispatch]
  );

  const handleTouchAll: NewOrderFormProps['handleTouchAll'] = useCallback(() => {
    dispatch(touchAll());
  }, [dispatch]);

  const handleTouchField: NewOrderFormProps['handleTouchField'] = useCallback(
    (field: string) => {
      dispatch(touchField(field));
    },
    [dispatch]
  );

  const security = form.symbolField.value;
  const marketDataSnapshotQuantity = form.quantityField.value || security?.NormalSize || '1';
  const marketDataSnapshotMarkets = useMemo(
    () =>
      form.marketAccountField.value?.SourceAccountID ? [form.marketAccountField.value.SourceAccountID] : EMPTY_ARRAY,
    [form.marketAccountField.value?.SourceAccountID]
  );
  const marketDataSnapshot = useMarketDataSnapshot({
    priceIncrement: security?.MinPriceIncrement,
    symbol: security?.Symbol,
    quantity: marketDataSnapshotQuantity,
    currency: form.orderCurrencyField.value,
    baseCurrency: security?.PositionCurrency || security?.BaseCurrency,
    tag: 'OMS/Order',
    // @ts-expect-error [sc-71645] Whitelabel UI Shouldn't Subscribe to "Market" Sub
    // All markets in Whitelabel are filtered to be Active
    markets: marketDataSnapshotMarkets,
  });

  const maxOrderSizeDialog = useDisclosure();
  const midPriceDialog = useDisclosure();

  const [midPriceToDisplay, setMidPriceToDisplay] = useState<BigSource | undefined | null>(null);
  const { midPrice, bidPrice, offerPrice } = useObservableValue(
    () =>
      marketDataSnapshot.marketDataSnapshots.pipe(
        scan<MarketDataSnapshot, { midPrice: BigSource | undefined | null; bidPrice?: string; offerPrice?: string }>(
          (res, snapshot: MarketDataSnapshot) => {
            const bidPrice = snapshot.Bids?.at(0)?.Price;
            const offerPrice = snapshot.Offers?.at(0)?.Price;
            const midPrice = calculateMidPrice(bidPrice, offerPrice);
            return { midPrice: midPrice, offerPrice, bidPrice };
          },
          { midPrice: null }
        )
      ),
    [marketDataSnapshot.marketDataSnapshots],
    { midPrice: null }
  );

  // We don't want to update the midprice whilst the dialog is showing.
  useEffect(() => {
    if (midPrice && !midPriceDialog.isOpen) {
      setMidPriceToDisplay(midPrice);
    }
  }, [midPrice, midPriceDialog.isOpen]);

  const submitForm: NewOrderFormProps['handleFormSubmit'] = useDynamicCallback(() => {
    if (orderViewType.value === 'order') {
      sendOrderForm();
    } else if (orderViewType.value === 'rfq') {
      mixpanel.track(MixpanelEvent.SendRfq);
      sendRFQForm();
    }
  });

  const maybeLimitPrice = useWLOrderFormSelector(selectLimitPrice);

  const handleFormSubmit = useDynamicCallback((step: CheckValidationStep) => {
    const shouldCheckReasonability =
      form.strategyField.value?.Name != null &&
      !DISABLED_REASONABILITY_CHECK_STRATEGIES_SET.has(form.strategyField.value.Name);

    // check mid price
    if (
      step <= CheckValidationStep.MIDPRICE &&
      shouldCheckReasonability &&
      !validateMidPriceDiff(midPrice, maybeLimitPrice?.value)
    ) {
      midPriceDialog.open();
      return;
    }
    // look up the trading limit thresholds
    if (step <= CheckValidationStep.ORDSIZE && warn) {
      maxOrderSizeDialog.open();
      return;
    }
    submitForm();
  });

  const balances = useWLOrderFormSelector(selectBalances);

  const globalCancelDialog = useGlobalCancelDialog();
  const canCancelCustomerOrder = useWLCanCancelCustomerOrder();
  const canModifyCustomerOrder = useWLCanModifyCustomerOrder();

  const activeOrder = useWLOrder({ orderID: focusedOrderID ?? undefined });

  const onCloseCard = useCallback(() => {
    dispatch(setFocusedOrderID(null));
  }, [dispatch]);

  const onCancelOrder = useDynamicCallback((order: CustomerOrder) => {
    if (!canCancelCustomerOrder(order)) {
      return;
    }
    mixpanel.track(MixpanelEvent.CancelOrder);
    orderService.cancelOrder(order.OrderID).catch(() => {
      globalCancelDialog.open(order.OrderID, true);
    });
  });

  const onArchiveOrder = useDynamicCallback((orderID: string) => {
    mixpanel.track(MixpanelEvent.ArchiveOrder);
    dispatch(archiveOrderID(orderID));
    dispatch(setFocusedOrderID(null));
  });

  const onResubmitOrder = useDynamicCallback((order: CustomerOrder) => {
    mixpanel.track(MixpanelEvent.OpenResubmitOrder);
    dispatch(resubmitOrderForm(order));
  });

  const onModifyOrder = useDynamicCallback((order: CustomerOrder) => {
    if (!canModifyCustomerOrder(order)) {
      return;
    }
    mixpanel.track(MixpanelEvent.OpenModifyOrder);
    dispatch(modifyOrder(order));
  });

  const currentQuote = useWLOrderFormSelector((state: WLRootState) => state.order.quote);

  const confirmMidPrice = useDynamicCallback(() => {
    handleFormSubmit(CheckValidationStep.ORDSIZE);
  });

  const confirmMaxOrderSize = useDynamicCallback(() => {
    handleFormSubmit(CheckValidationStep.NONE);
  });

  const loading = orderStep === 'loading';
  const newConfirmation = orderStep === 'new_confirmation';
  const updateConfirmation = orderStep === 'update_confirmation';
  const updateRejected = orderStep === 'update_rejected';
  const newRejected = orderStep === 'new_rejected';
  const rfqExpired = orderStep === 'rfq_expired';
  const cancelFailed = orderStep === 'cancel_failed';
  const cancelling = orderStep === 'cancel_pending';
  const canceled = orderStep === 'cancel_confirmation';

  const showOverlay =
    loading ||
    newConfirmation ||
    updateConfirmation ||
    updateRejected ||
    newRejected ||
    cancelFailed ||
    cancelling ||
    rfqExpired ||
    canceled;

  const handleClickSubmitForm = useCallback(() => {
    handleFormSubmit(CheckValidationStep.MIDPRICE);
  }, [handleFormSubmit]);

  const error = useWLOrderFormSelector(selectError);
  const { toasts, add: addToast, remove: removeToast } = useToasts();
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (error && error.text === 'no_order_response') {
      logger.error(new Error(`Did not receive order back with matching ClOrdID: ${error.clOrderID}`), {
        extra: { clOrderID: error.clOrderID },
      });
      addToast({
        id: uuid(),
        text: formatMessage(messages.slowToRespondToast),
        variant: NotificationVariants.Warning,
        dismissable: true,
        timeout: 10_000,
      });
      dispatch(setError(null));
    }
  }, [error, addToast, dispatch, formatMessage]);

  const { pathname } = useLocation();
  const pathNameRef = useRef<string>();
  const isMobile = useShouldRenderMobile();
  useEffect(() => {
    // If we are on mobile, this acts like a reset for the order form if the user
    // moves to a different tab and we are showing an overlay or currently quoting
    if (isMobile && pathname !== pathNameRef.current && (showOverlay || !!quoteReqID)) {
      handleClearForm();
    }
    pathNameRef.current = pathname;
  }, [handleClearForm, isMobile, pathname, quoteReqID, showOverlay]);

  return (
    <OrderFormsWrapper>
      {showOverlay && (
        <Overlay data-testid="overlay-order-form">
          {loading && (
            <MessageWrapper data-testid="overlay-message-sending-order">
              <Spinner />
              <FeedbackMessage>
                <FormattedMessage {...messages.sendingOrder} />
              </FeedbackMessage>
            </MessageWrapper>
          )}
          {newConfirmation && (
            <MessageWrapper data-testid="overlay-message-confirm">
              <Checkmark />
              <FeedbackMessage>
                {activeOrder?.OrdStatus === OrdStatusEnum.Filled ? (
                  <FormattedMessage {...messages.orderFilled} />
                ) : (
                  <FormattedMessage {...messages.orderSent} />
                )}
              </FeedbackMessage>
            </MessageWrapper>
          )}
          {newRejected && (
            <MessageWrapper data-testid="overlay-message-rejected">
              <Crossmark />
              <FeedbackMessage>{newRejected && <FormattedMessage {...messages.orderRejected} />}</FeedbackMessage>
            </MessageWrapper>
          )}
          {updateConfirmation && (
            <MessageWrapper data-testid="overlay-message-updated-order">
              <Checkmark />
              <FeedbackMessage>
                <FormattedMessage {...messages.orderUpdated} />
              </FeedbackMessage>
            </MessageWrapper>
          )}

          {updateRejected && (
            <MessageWrapper data-testid="overlay-message-updated-rejected">
              <Crossmark />
              <FeedbackMessage>
                <FormattedMessage {...messages.modifyRejected} />
              </FeedbackMessage>
            </MessageWrapper>
          )}
          {rfqExpired && (
            <MessageWrapper data-testid="overlay-message-quote-expired">
              <Crossmark />
              <FeedbackMessage>
                <FormattedMessage {...messages.quoteExpired} />
              </FeedbackMessage>
            </MessageWrapper>
          )}

          {cancelFailed && (
            <MessageWrapper data-testid="overlay-message-cancel-failed">
              <Crossmark />
              <FeedbackMessage>{cancelFailed && <FormattedMessage {...messages.cancelFailed} />}</FeedbackMessage>
            </MessageWrapper>
          )}
          {cancelling && (
            <MessageWrapper data-testid="overlay-message-cancelling">
              <Spinner />
              <FeedbackMessage>
                <FormattedMessage {...messages.cancelOrder} />
              </FeedbackMessage>
            </MessageWrapper>
          )}
          {canceled && (
            <MessageWrapper data-testid="overlay-message-canceled">
              <Checkmark />
              <FeedbackMessage>
                <FormattedMessage {...messages.orderCanceled} />
              </FeedbackMessage>
            </MessageWrapper>
          )}
        </Overlay>
      )}

      <NewOrderForm
        quoteReqID={quoteReqID}
        handleSymbolChange={handleSymbolChange}
        handleTouchAll={handleTouchAll}
        handleTouchField={handleTouchField}
        handleClearForm={handleClearForm}
        handleFormSubmit={handleClickSubmitForm}
        handleQuantityChange={handleQuantityChange}
        handleOrderCurrencyChange={handleOrderCurrencyChange}
        handleSideChange={handleSideChange}
        handleStrategyChange={handleStrategyChange}
        handleStrategyParamChange={handleStrategyParamChange}
        handleMarketAccountChange={handleMarketAccountChange}
        marketDataSnapshot={marketDataSnapshot}
        orderCcyField={form.orderCurrencyField}
        parameterFields={form.parameters}
        symbolField={form.symbolField}
        marketAccountField={form.marketAccountField}
        quantityField={form.quantityField}
        security={form.symbolField.value}
        sideField={form.sideField}
        strategyField={form.strategyField}
        orderFormValid={orderFormValid}
        orderStep={orderStep}
        orderBeingModified={orderBeingModified}
        balances={balances}
        balanceDetails={
          security != null && !isCFD(security) ? (
            <BalanceDetails accountID={form.marketAccountField.value?.SourceAccountID} security={security} />
          ) : null
        }
        orderSummary={<OrderSummary bidPrice={bidPrice} offerPrice={offerPrice} />}
        orderViewType={orderViewType}
        handleViewTypeChange={handleViewTypeChange}
        pickQuoteView={orderViewType.value === 'rfq' && quoteReqID ? <PickQuoteView quote={currentQuote} /> : null}
        isMobile={isMobile}
      />
      {activeOrder && (
        <Box zIndex={OVERLAY_Z_INDEX + 1}>
          <ExecutionCard
            attached={true}
            order={activeOrder}
            onCloseCard={onCloseCard}
            onCancelOrder={onCancelOrder}
            onArchiveOrder={onArchiveOrder}
            onResubmitOrder={onResubmitOrder}
            onModifyOrder={onModifyOrder}
          />
        </Box>
      )}

      <Dialog
        width={OMS_DIALOG_WIDTH}
        usePortal={false}
        {...midPriceDialog}
        onConfirm={confirmMidPrice}
        title="Confirmation"
        stretchButtons
      >
        <VStack gap="spacing16">
          <div>
            <FormattedMessage
              {...messages.midPriceWarning}
              values={{
                quoteCurrency: security?.QuoteCurrency,
                limitPrice: format(maybeLimitPrice?.value, {
                  spec: security?.DefaultPriceIncrement,
                  pretty: true,
                }),
                midPrice: format(midPriceToDisplay, {
                  spec: security?.DefaultPriceIncrement,
                  pretty: true,
                  round: true,
                }),
              }}
            />
          </div>
          <div>
            <FormattedMessage {...messages.continueConfirmation} />
          </div>
        </VStack>
      </Dialog>
      <Dialog
        {...maxOrderSizeDialog}
        usePortal={false}
        onConfirm={confirmMaxOrderSize}
        title="Threshold Warning"
        stretchButtons
      >
        {limit && (
          <>
            <Text color="colorTextAttention">
              <FormattedMessage {...messages.thresholdWarning} />
            </Text>
            <Box mt="spacing16">
              <HStack justifyContent="space-between">
                <FormattedMessage {...messages.warningThreshold} />
                <InlineFormattedNumber
                  number={limit.WarnThreshold}
                  currency={limit.ThresholdCurrency}
                  increment={limit.Currency ? currenciesBySymbol.get(limit.Currency)?.DefaultIncrement : undefined}
                />
              </HStack>
              <Divider mt="spacing8" />
            </Box>
          </>
        )}
      </Dialog>
      {toasts?.length > 0 && (
        <OmsToasts>
          <Toasts toasts={toasts} remove={removeToast} />
        </OmsToasts>
      )}
    </OrderFormsWrapper>
  );
};
